/*
 * Cart drawer redesign: the "+ Add" button on the upsell cards, and the mobile
 * upsell rail.
 *
 * Single variant products add in one click. Multi variant products render the
 * same button as a link to the product page instead, so nobody is ever sent a
 * variant they did not choose, and this file never sees them.
 *
 * The drawer refreshes itself: theme:cartchanged is the event Symmetry's own
 * cart-form element listens for, so there is no second fetch loop here and no
 * markup is re-rendered by this file.
 */
(function () {
  'use strict';

  var BUSY_CLASS = 'cart-drawer__upsell-add--busy';

  function addUrl() {
    if (window.theme && window.theme.routes && window.theme.routes.cart_add_url) {
      return window.theme.routes.cart_add_url;
    }
    return '/cart/add.js';
  }

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    var button = target.closest('[data-cart-drawer-add]');
    if (!button) return;

    event.preventDefault();
    if (button.classList.contains(BUSY_CLASS)) return;

    var variantId = button.getAttribute('data-cart-drawer-add');
    if (!variantId) return;

    button.classList.add(BUSY_CLASS);

    var done = function () {
      button.classList.remove(BUSY_CLASS);
    };

    fetch(addUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.json();
      })
      .then(function () {
        document.dispatchEvent(
          new CustomEvent('theme:cartchanged', { bubbles: true, cancelable: false })
        );
        done();
      })
      .catch(done);
  });

  /*
   * Mobile upsell rail, spec 7.1 item 5.
   *
   * Under 640px the cross sell list becomes a native horizontal scroller with
   * snap points and the next card peeking. Owl owns the track transform on this
   * theme and swallows a horizontal drag, so a scroller cannot be layered on top
   * of it. The list is kept out of Owl at this width instead, and handed back to
   * Owl the moment the viewport grows past it.
   *
   * This is a custom element because Symmetry re-renders the drawer's cross sell
   * markup through theme.mergeNodes, which replaces this node. connectedCallback
   * is the only hook that survives that, and it runs long before
   * cc-cart-cross-sell's own 500ms timer reaches theme.loadCarousels.
   */
  var RAIL_CLASS = 'cart-item-upsells--rail';
  var RAIL_QUERY = '(max-width: 639px)';
  var OWL_CLASSES = ['carousel', 'owl-carousel', 'owl-loaded', 'owl-drag', 'owl-grab', 'owl-rtl'];

  // Mirrors theme.js's own carousel bootstrap, which is the only place these
  // cards are taken off manual loading. Without it a rail that never met Owl
  // would render empty image frames.
  var MANUAL_IMAGES =
    '.product-block__image--primary .lazyload--manual,' +
    ' .product-block__image--secondary[data-image-index="1"] .lazyload--manual,' +
    ' .product-block-options__item.lazyload--manual';

  function jq() {
    return window.jQuery || null;
  }

  if (window.customElements && !window.customElements.get('cart-upsell-rail')) {
    class CartUpsellRail extends HTMLElement {
      connectedCallback() {
        this.query = window.matchMedia(RAIL_QUERY);
        this.onQueryChange = this.sync.bind(this);
        if (this.query.addEventListener) {
          this.query.addEventListener('change', this.onQueryChange);
        } else if (this.query.addListener) {
          this.query.addListener(this.onQueryChange);
        }
        this.sync();
      }

      disconnectedCallback() {
        this.stopWatching();
        if (!this.query) return;
        if (this.query.removeEventListener) {
          this.query.removeEventListener('change', this.onQueryChange);
        } else if (this.query.removeListener) {
          this.query.removeListener(this.onQueryChange);
        }
      }

      sync() {
        var list = this.querySelector('.product-list');
        if (!list) return;
        if (this.query.matches) {
          this.toRail(list);
        } else {
          this.toCarousel(list);
        }
      }

      toRail(list) {
        if (this.classList.contains(RAIL_CLASS)) return;

        var $ = jq();
        var owl = $ ? $(list).data('owl.carousel') : null;
        if (owl) {
          // theme.loadCarousels delegates the chevron clicks here, and they have
          // no carousel to drive once Owl is gone.
          $(this.querySelector('.collection-slider')).off('.themeCarousel');
          owl.destroy();
        }

        for (var i = 0; i < OWL_CLASSES.length; i++) {
          list.classList.remove(OWL_CLASSES[i]);
        }
        this.classList.add(RAIL_CLASS);

        if (list.children.length) {
          this.loadImages(list);
        } else {
          this.watchForCards(list);
        }
      }

      toCarousel(list) {
        this.stopWatching();
        if (!this.classList.contains(RAIL_CLASS)) return;

        this.classList.remove(RAIL_CLASS);
        list.classList.add('carousel');

        var $ = jq();
        if ($) $(this.querySelector('.collection-slider')).off('.themeCarousel');
        if (window.theme && typeof window.theme.loadCarousels === 'function') {
          window.theme.loadCarousels(this);
        }
      }

      // cc-cart-cross-sell fills the list over the network, so on the automatic
      // path the cards land after this element connects. One insertion is all
      // there is to wait for, so the observer stops itself.
      watchForCards(list) {
        if (this.observer || !window.MutationObserver) return;
        var self = this;
        this.observer = new MutationObserver(function () {
          if (!list.children.length) return;
          self.stopWatching();
          if (self.classList.contains(RAIL_CLASS)) self.loadImages(list);
        });
        this.observer.observe(list, { childList: true });
      }

      stopWatching() {
        if (!this.observer) return;
        this.observer.disconnect();
        this.observer = null;
      }

      loadImages(list) {
        var manual = list.querySelectorAll(MANUAL_IMAGES);
        for (var i = 0; i < manual.length; i++) {
          manual[i].classList.remove('lazyload--manual');
          manual[i].classList.add('lazyload');
        }

        var section = this.closest('[data-section-type]');
        if (section && window.theme && window.theme.ProductBlockManager) {
          window.theme.ProductBlockManager.loadImages(section);
        }
      }
    }

    window.customElements.define('cart-upsell-rail', CartUpsellRail);
  }
})();
